import { useId, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PlantacionDeInforme } from "@/lib/informes";
import { cn } from "@/lib/utils";

import { claveDeEspecie, especiesSugeridas } from "./plantaciones";

interface SelectorDeEspecieProps {
  valor: string;
  /** Las filas ya cargadas: de ahí salen las sugerencias. */
  filas: PlantacionDeInforme[];
  disabled: boolean;
  /** Es obligatorio y está vacío: el campo se resalta en ámbar. */
  faltante?: boolean;
  onChange: (valor: string) => void;
}

/**
 * El campo "Especie", con la ayuda que evita que el mismo huerto quede partido
 * en dos grupos.
 *
 * ES UN CAMPO DE TEXTO CON SUGERENCIAS, NO UN SELECTOR CERRADO, y la
 * diferencia es toda la función. No hay maestro de especies —decisión
 * explícita del stakeholder: "sin una tabla nueva en la bd, es solo UX"—, así
 * que se puede escribir cualquier cosa. Lo que la lista hace es MOSTRAR lo que
 * ya se escribió en este informe mientras se teclea, para que quien carga la
 * segunda fila de cerezos elija la misma palabra que puso en la primera.
 *
 * EL DEFECTO QUE PREVIENE NO HACE RUIDO: "Cerezos" y "Cerezo" son dos claves
 * distintas, así que el cuadro mostraría el mismo huerto en dos grupos, con
 * dos subtotales, y ninguna cifra estaría mal. Solo el total general seguiría
 * cuadrando — que es justo lo que impide notarlo.
 *
 * NO ES UN `<datalist>`. Se probó mentalmente y no sirve: su despliegue lo
 * dibuja el navegador con su propio estilo (fuera del DS), no se puede recorrer
 * con el teclado de forma predecible entre navegadores, y sobre todo NO EXISTE
 * EN EL DOM para los tests — en happy-dom un `<datalist>` es un elemento vacío,
 * así que no habría manera de custodiar que la sugerencia aparece.
 *
 * SE ABRE AL ESCRIBIR Y AL ENFOCAR, no con un botón: el campo se rellena
 * tecleando y un botón aparte pediría un gesto de más para una ayuda que tiene
 * que estar donde ya está la mano.
 *
 * EL CIERRE VA EN `onBlur` CON `relatedTarget`, y no con un `setTimeout`: un
 * temporizador cierra la lista antes de que el clic llegue al botón cuando la
 * máquina está cargada, y el síntoma es una sugerencia que "a veces no se
 * puede elegir". Preguntando a dónde se fue el foco, elegir SIEMPRE funciona.
 */
export function SelectorDeEspecie({
  valor,
  filas,
  disabled,
  faltante = false,
  onChange,
}: SelectorDeEspecieProps) {
  const id = useId();
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  const sugerencias = especiesSugeridas(filas, valor);

  /* SOLO AVISA CUANDO HAY ALGO PARECIDO Y NO ES LO MISMO. Con el campo vacío
     la lista son "todas las especies del informe", que no es un parecido: es
     un menú. Ahí no corresponde advertir nada. */
  const clave = claveDeEspecie(valor);
  const yaExiste = filas.some((fila) => claveDeEspecie(fila.especie) === clave) && clave !== "";

  function elegir(especie: string) {
    onChange(especie);
    setAbierto(false);
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Especie</Label>

      <div ref={caja} className="relative">
        <Input
          id={id}
          value={valor}
          disabled={disabled}
          placeholder="Ej: Cerezos"
          autoComplete="off"
          className={cn(faltante && "border-warning-dot/60")}
          role="combobox"
          aria-expanded={abierto && sugerencias.length > 0}
          aria-controls={`${id}-sugerencias`}
          onChange={(e) => {
            onChange(e.target.value);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
          onBlur={(e) => {
            // Si el foco se fue a un botón de la propia lista, no se cierra:
            // ese clic todavía no terminó de aplicarse.
            if (!caja.current?.contains(e.relatedTarget)) setAbierto(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setAbierto(false);
          }}
        />

        {abierto && sugerencias.length > 0 ? (
          <ul
            id={`${id}-sugerencias`}
            role="listbox"
            aria-label="Especies ya usadas en este informe"
            className="bg-card absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border py-1 shadow-md"
          >
            {sugerencias.map((especie) => (
              <li key={especie}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="hover:bg-muted focus:bg-muted w-full px-3 py-1.5 text-left text-sm"
                  onClick={() => elegir(especie)}
                >
                  {especie}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* LA CONFIRMACIÓN IMPORTA TANTO COMO LA SUGERENCIA: dice que esta fila
          va a caer en un grupo que ya existe, que es la pregunta que uno se
          hace al escribir la especie de la segunda plantación. */}
      <p className="text-ink-hint text-caption">
        {yaExiste
          ? "Se agrupa con las plantaciones de esta misma especie."
          : "Agrupa el cuadro. Escríbela igual que en las otras filas de la misma especie."}
      </p>
    </div>
  );
}
